/**
 * Preset-driven date range picker for analytics tabs. Emits range keys
 * (`7d`/`30d`/etc) that map directly to the backend's `?range=` query param.
 *
 * Custom-range input is a pair of native date inputs behind a "Custom" preset.
 * Keeping this minimal — the rollup data is day-grained so hour/minute UI
 * would be noise.
 */
import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '../../../../lib/cn';
import {
  PRESET_ORDER,
  type AnalyticsRange,
  type RangeKey,
  presetLabel,
} from '../hooks/useAnalyticsRange';

export interface DateRangePickerProps {
  range: AnalyticsRange;
  className?: string;
  /** Restrict visible presets; defaults to PRESET_ORDER. */
  presets?: Exclude<RangeKey, 'custom'>[];
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  range,
  className,
  presets = PRESET_ORDER,
}) => {
  const [customOpen, setCustomOpen] = useState(false);

  return (
    <div
      className={cn(
        'inline-flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-1 shadow-sm',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Calendar className="ml-2 h-4 w-4 text-gray-400" aria-hidden />
        <div className="flex items-center gap-0.5">
          {presets.map((p) => {
            const active = range.rangeKey === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => range.setRange(p)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                  active
                    ? 'bg-coach-cedar text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                {presetLabel(p)}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
              range.rangeKey === 'custom'
                ? 'bg-coach-cedar text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100',
            )}
          >
            Custom
          </button>
        </div>
      </div>
      {customOpen && <CustomRangeInline range={range} onClose={() => setCustomOpen(false)} />}
    </div>
  );
};

const CustomRangeInline: React.FC<{ range: AnalyticsRange; onClose: () => void }> = ({
  range,
  onClose,
}) => {
  const [start, setStart] = useState(() => toInputDate(range.start));
  const [end, setEnd] = useState(() => toInputDate(range.end));

  const apply = () => {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.valueOf()) || isNaN(e.valueOf()) || s > e) return;
    range.setCustomRange(s, e);
    onClose();
  };

  return (
    <div className="flex items-center gap-1.5 border-t border-gray-200 pt-1.5 pl-1">
      <input
        type="date"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        className="rounded-md border border-gray-200 px-1.5 py-1 text-xs text-gray-700 focus:border-coach-cedar focus:outline-none"
      />
      <span className="text-xs text-gray-400">→</span>
      <input
        type="date"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        className="rounded-md border border-gray-200 px-1.5 py-1 text-xs text-gray-700 focus:border-coach-cedar focus:outline-none"
      />
      <button
        type="button"
        onClick={apply}
        className="rounded-md bg-coach-cedar px-2.5 py-1 text-xs font-semibold text-white hover:bg-coach-cedar-dark"
      >
        Apply
      </button>
    </div>
  );
};

function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
